import { Search } from '@/DTOS/MentenancePlanning/Search';
import { DatesSetArg, EventClickArg, EventContentArg, EventDropArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useQuery } from '@tanstack/react-query';
import { App, Button, Card, Col, DatePicker, Row, Segmented, Select, Space, Spin, Tag, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';
import UserService from '../../../../services/user/UserService';
import './MentenancePlanningCalendarTab.css';

const { Text } = Typography;

type CalendarViewMode = 'week' | 'month' | 'year';

const VIEW_TO_FULLCALENDAR: Record<CalendarViewMode, string> = {
  week: 'dayGridWeek',
  month: 'dayGridMonth',
  year: 'multiMonthYear',
};

const getWeekStart = (value: Dayjs) => {
  const dayOfWeek = value.day();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  return value.add(diffToMonday, 'day').startOf('day');
};

interface MaintenanceCalendarRow {
  id: string;
  number?: string;
  statusId?: number;
  status?: { id?: number; name?: string } | null;
  interventionFile?: {
    id?: string | null;
    deliveryDate?: string | null;
    helionEngineers?: any[] | null;
  } | null;
  periodicity?: string | null;
  dateEnd?: string | null;
  finalDate?: string | null;
  appointment?: string | null;
  intervalTimetable?: string | null;
  history?: string[] | null;
  contractId?: string | null;
  companyId?: string | null;
  workPlaceId?: string | null;
  assignedTechnicianId?: string | null;
  registeredByUserId?: string | null;
  interventionFileId?: string | null;
  metadata?: any;
  company?: any;
  workPlace?: any;
  helionEngineers?: any[] | null;
  assignedTechnician?: any;
  mentenancePlanningSystemTypes?: any[];
}

interface StatusLegendItem {
  statusId: number;
  label: string;
  color: string;
  count: number;
}

const STATUS_CONFIG: Record<number, { label: string; color: string }> = {
  1: { label: 'Netransmis', color: '#8c8c8c' },
  2: { label: 'Transmis', color: '#1677ff' },
  3: { label: 'A ajuns', color: '#52c41a' },
  4: { label: 'Finalizata', color: '#13c2c2' },
  5: { label: 'Cerere reprogramare', color: '#faad14' },
};

const PERIODICITY_OPTIONS = ['lunar', 'trimestrial', 'semestrial', 'anual', 'la cerere'];

const getStatusDisplay = (statusId?: number, fallbackName?: string) => {
  if (statusId && STATUS_CONFIG[statusId]) {
    return STATUS_CONFIG[statusId];
  }

  return {
    label: fallbackName || 'Status necunoscut',
    color: '#722ed1',
  };
};

const formatDateForApi = (value: Dayjs) => value.format('YYYY-MM-DD');

const getClientName = (record: MaintenanceCalendarRow) =>
  record?.metadata?.company?.name ?? record?.company?.name ?? 'Client necunoscut';

const getWorkPlaceName = (record: MaintenanceCalendarRow) =>
  record?.metadata?.workPlace?.name ?? record?.workPlace?.name ?? 'Punct necunoscut';

const getSystemNames = (record: MaintenanceCalendarRow) => {
  const systemNames = record?.mentenancePlanningSystemTypes
    ?.map((entry: any) => entry?.systemType?.name)
    ?.filter((name: string) => !!name);

  return systemNames && systemNames.length ? Array.from(new Set(systemNames)) : [];
};

const getAssignedTechnicianId = (record: MaintenanceCalendarRow) =>
  record?.assignedTechnicianId ?? record?.assignedTechnician?.id ?? undefined;

const getAssignedTechnicianName = (record: MaintenanceCalendarRow) => record?.assignedTechnician?.name ?? 'Nealocat';

const getMaintenanceStatusId = (record: MaintenanceCalendarRow) => record?.statusId ?? record?.status?.id;

const getMaintenanceCalendarDate = (record: MaintenanceCalendarRow) => {
  const statusId = getMaintenanceStatusId(record);
  return statusId === 4 ? record?.interventionFile?.deliveryDate : record?.appointment || record?.dateEnd;
};

const getInterventionEngineers = (record: MaintenanceCalendarRow) => {
  const items = [...(record?.interventionFile?.helionEngineers ?? []), ...(record?.helionEngineers ?? [])];
  const uniqueByUserId = new Map<string, any>();
  items.forEach((engineer: any) => {
    const userId = engineer?.userId ?? engineer?.user?.id;
    if (userId && !uniqueByUserId.has(userId)) {
      uniqueByUserId.set(userId, engineer);
    }
  });
  return Array.from(uniqueByUserId.values());
};

const getInterventionEngineerIds = (record: MaintenanceCalendarRow) =>
  getInterventionEngineers(record)
    .map((engineer: any) => engineer?.userId ?? engineer?.user?.id)
    .filter((id: string | undefined | null): id is string => !!id);

const getInterventionEngineerNames = (record: MaintenanceCalendarRow, technicians: any[] = []) => {
  const names = getInterventionEngineers(record)
    .map((engineer: any) => {
      const userId = engineer?.userId ?? engineer?.user?.id;
      return engineer?.user?.name ?? technicians.find((technician: any) => technician?.id === userId)?.name;
    })
    .filter((name: string | undefined | null): name is string => !!name);

  return Array.from(new Set(names));
};

const canEditMaintenance = (record: MaintenanceCalendarRow) => {
  const lockedStatuses = [4];
  return !lockedStatuses.includes(getMaintenanceStatusId(record) ?? -1);
};

const CALENDAR_STATUS_IDS = [2, 3, 4, 5] as const;

const MentenancePlanningCalendarTab: React.FC = () => {
  const { message } = App.useApp();
  const calendarRef = useRef<FullCalendar | null>(null);

  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [focusDate, setFocusDate] = useState<Dayjs>(() => dayjs());
  const [selectedPeriodicityFilter, setSelectedPeriodicityFilter] = useState<string | undefined>(undefined);
  const [selectedTechnicianFilterId, setSelectedTechnicianFilterId] = useState<string | undefined>(undefined);
  const [selectedStatusFilterIds, setSelectedStatusFilterIds] = useState<number[]>([]);

  const [selectedMaintenanceIds, setSelectedMaintenanceIds] = useState<string[]>([]);
  const [bulkTechnicianId, setBulkTechnicianId] = useState<string | undefined>(undefined);
  const [bulkAppointmentDate, setBulkAppointmentDate] = useState<Dayjs | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const effectiveQueryStartDate = useMemo(() => {
    if (viewMode === 'week') return getWeekStart(focusDate);
    if (viewMode === 'year') return focusDate.startOf('year');
    return focusDate.startOf('month');
  }, [viewMode, focusDate]);

  const effectiveQueryEndDate = useMemo(() => {
    if (viewMode === 'week') return getWeekStart(focusDate).add(6, 'day').endOf('day');
    if (viewMode === 'year') return focusDate.endOf('year');
    return focusDate.endOf('month');
  }, [viewMode, focusDate]);

  const isMonthView = viewMode === 'month';
  const isFinalizedStatusVisible = selectedStatusFilterIds.length === 0 || selectedStatusFilterIds.includes(4);

  const { data: technicians = [] } = useQuery({
    queryKey: ['maintenanceCalendarTechnicians'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: 'tehnician, senior_tehnician',
      }).then((data) => data?.response ?? []),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const searchPayload: Search = useMemo(
    () => ({
      isCompanyIncluded: true,
      isWorkPlaceIncluded: true,
      isAssignedTechnicianIncluded: true,
      isStatusIncluded: true,
      isInterventionFileIncluded: true,
      isMentenancePlanningSystemTypeIncluded: true,
      statusIds:
        selectedStatusFilterIds.length > 0
          ? selectedStatusFilterIds.filter((id) => CALENDAR_STATUS_IDS.includes(id as 2 | 3 | 4 | 5))
          : [...CALENDAR_STATUS_IDS],
      // Keep technician filtering client side (status 4 uses intervention file engineers, not only assigned technician).
      assignedTechnicianId: undefined,
      periodicity: selectedPeriodicityFilter || undefined,
      // Backend filters by DateEnd; when status 4 is visible we fetch broader and filter by event date on client.
      startDate: isFinalizedStatusVisible ? undefined : formatDateForApi(effectiveQueryStartDate),
      endDate: isFinalizedStatusVisible ? undefined : formatDateForApi(effectiveQueryEndDate),
      skip: 0,
      take: 5000,
      sortDirection: 'asc',
    }),
    [
      effectiveQueryStartDate,
      effectiveQueryEndDate,
      selectedPeriodicityFilter,
      selectedStatusFilterIds,
      isFinalizedStatusVisible,
    ],
  );

  const {
    data: maintenancesResponse,
    isFetching: isLoadingMaintenances,
    refetch,
  } = useQuery({
    queryKey: [
      'maintenanceCalendarData',
      effectiveQueryStartDate.format('YYYY-MM-DD'),
      effectiveQueryEndDate.format('YYYY-MM-DD'),
      selectedPeriodicityFilter || 'all',
      selectedStatusFilterIds
        .slice()
        .sort((left, right) => left - right)
        .join(',') || 'all',
    ],
    queryFn: () => MentenancePlanningService.searchAll(searchPayload),
    refetchOnWindowFocus: false,
  });

  const maintenances: MaintenanceCalendarRow[] = maintenancesResponse?.response ?? [];

  const maintenancesById = useMemo(() => {
    const map = new Map<string, MaintenanceCalendarRow>();
    maintenances.forEach((record) => map.set(record.id, record));
    return map;
  }, [maintenances]);

  const maintenancesMatchingBaseFilters = useMemo(() => {
    const normalizedPeriodicityFilter = (selectedPeriodicityFilter ?? '').trim().toLowerCase();

    return maintenances.filter((record) => {
      const statusId = getMaintenanceStatusId(record);
      if (selectedStatusFilterIds.length > 0 && (!statusId || !selectedStatusFilterIds.includes(statusId))) {
        return false;
      }

      const eventDate = getMaintenanceCalendarDate(record);
      if (!eventDate || !dayjs(eventDate).isValid()) {
        return false;
      }

      const eventDay = dayjs(eventDate).startOf('day');
      const isInRange =
        !eventDay.isBefore(effectiveQueryStartDate.startOf('day')) && !eventDay.isAfter(effectiveQueryEndDate.endOf('day'));

      if (!isInRange) {
        return false;
      }

      if (normalizedPeriodicityFilter) {
        const recordPeriodicity = (record?.periodicity ?? '').trim().toLowerCase();
        if (recordPeriodicity !== normalizedPeriodicityFilter) {
          return false;
        }
      }

      return true;
    });
  }, [maintenances, selectedPeriodicityFilter, selectedStatusFilterIds, effectiveQueryStartDate, effectiveQueryEndDate]);

  const technicianCountById = useMemo(() => {
    const countMap = new Map<string, { name: string; count: number }>();

    maintenancesMatchingBaseFilters.forEach((record) => {
      const statusId = getMaintenanceStatusId(record);
      const technicianIds = new Set<string>();
      if (statusId === 4) {
        getInterventionEngineerIds(record).forEach((id) => technicianIds.add(id));
      } else {
        const assignedTechnicianId = getAssignedTechnicianId(record);
        if (assignedTechnicianId) {
          technicianIds.add(assignedTechnicianId);
        }
      }

      technicianIds.forEach((technicianId) => {
        const existing = countMap.get(technicianId);
        const fallbackTechnician = technicians.find((technician: any) => technician?.id === technicianId);
        const finalizedNames = getInterventionEngineerNames(record, technicians);
        const technicianName = finalizedNames[0] ?? fallbackTechnician?.name ?? getAssignedTechnicianName(record) ?? 'Tehnician';

        if (existing) {
          existing.count += 1;
        } else {
          countMap.set(technicianId, { name: technicianName, count: 1 });
        }
      });
    });

    return countMap;
  }, [maintenancesMatchingBaseFilters, technicians]);

  const technicianFilterOptions = useMemo(
    () =>
      (technicians ?? [])
        .map((technician: any) => {
          const item = technicianCountById.get(technician?.id);
          return {
            value: technician?.id,
            label: `${technician?.name ?? 'Tehnician'} (${item?.count ?? 0})`,
            count: item?.count ?? 0,
            name: technician?.name ?? 'Tehnician',
          };
        })
        .sort((left, right) => left.name.localeCompare(right.name, 'ro', { sensitivity: 'base' })),
    [technicianCountById, technicians],
  );

  const filteredMaintenances = useMemo(() => {
    return maintenancesMatchingBaseFilters.filter((record) => {
      if (!selectedTechnicianFilterId) {
        return true;
      }

      if (getMaintenanceStatusId(record) === 4) {
        return getInterventionEngineerIds(record).includes(selectedTechnicianFilterId);
      }

      const assignedTechnicianId = getAssignedTechnicianId(record);
      if (assignedTechnicianId === selectedTechnicianFilterId) {
        return true;
      }

      return false;
    });
  }, [maintenancesMatchingBaseFilters, selectedTechnicianFilterId]);

  const legendItems = useMemo<StatusLegendItem[]>(() => {
    const countByStatus = new Map<number, number>();
    filteredMaintenances.forEach((record) => {
      const statusId = getMaintenanceStatusId(record) ?? 0;
      countByStatus.set(statusId, (countByStatus.get(statusId) ?? 0) + 1);
    });
    return CALENDAR_STATUS_IDS.map((statusId) => ({
      statusId,
      label: STATUS_CONFIG[statusId].label,
      color: STATUS_CONFIG[statusId].color,
      count: countByStatus.get(statusId) ?? 0,
    }));
  }, [filteredMaintenances]);

  const filteredMaintenancesById = useMemo(() => {
    const map = new Map<string, MaintenanceCalendarRow>();
    filteredMaintenances.forEach((record) => map.set(record.id, record));
    return map;
  }, [filteredMaintenances]);

  const calendarEvents = useMemo(() => {
    return filteredMaintenances
      .map((record) => {
        const statusId = getMaintenanceStatusId(record);
        const eventDate = getMaintenanceCalendarDate(record);
        if (!eventDate || !dayjs(eventDate).isValid()) {
          return null;
        }

        const statusDisplay = getStatusDisplay(statusId, record?.status?.name);

        return {
          id: record.id,
          title: `#${record.number || '-'} ${getClientName(record)} / ${getWorkPlaceName(record)}`,
          start: dayjs(eventDate).format('YYYY-MM-DD'),
          allDay: true,
          backgroundColor: statusDisplay.color,
          borderColor: statusDisplay.color,
          textColor: '#ffffff',
          startEditable: canEditMaintenance(record),
          extendedProps: {
            record,
            statusLabel: statusDisplay.label,
          },
        };
      })
      .filter((event): event is NonNullable<typeof event> => event !== null);
  }, [filteredMaintenances]);

  const selectedMaintenances = useMemo(
    () =>
      selectedMaintenanceIds
        .map((maintenanceId) => maintenancesById.get(maintenanceId))
        .filter((record): record is MaintenanceCalendarRow => !!record),
    [selectedMaintenanceIds, maintenancesById],
  );

  const selectedVisibleMaintenances = useMemo(
    () =>
      selectedMaintenanceIds
        .map((maintenanceId) => filteredMaintenancesById.get(maintenanceId))
        .filter((record): record is MaintenanceCalendarRow => !!record),
    [selectedMaintenanceIds, filteredMaintenancesById],
  );

  const selectedBulkEligibleMaintenances = useMemo(
    () => selectedMaintenances.filter(canEditMaintenance),
    [selectedMaintenances],
  );

  useEffect(() => {
    setSelectedMaintenanceIds((previous) => previous.filter((maintenanceId) => maintenancesById.has(maintenanceId)));
  }, [maintenancesById]);

  const updateMaintenanceRecord = async (
    record: MaintenanceCalendarRow,
    updates: { appointment?: string; assignedTechnicianId?: string },
  ) => {
    const companyId = record?.companyId ?? record?.company?.id ?? record?.metadata?.company?.id;
    const workPlaceId = record?.workPlaceId ?? record?.workPlace?.id ?? record?.metadata?.workPlace?.id;
    const systemTypes = record?.mentenancePlanningSystemTypes ?? [];
    const periodicity = record?.periodicity;
    const dateEndSource = record?.dateEnd || record?.appointment || updates?.appointment;
    const dateEnd = dateEndSource ? dayjs(dateEndSource).format('YYYY-MM-DD') : null;
    const nextAppointmentSource = updates.appointment ?? record.appointment ?? record.dateEnd ?? null;
    const nextAppointment = nextAppointmentSource ? dayjs(nextAppointmentSource).format('YYYY-MM-DD') : null;

    if (!record?.id || !companyId || !workPlaceId || !periodicity || !dateEnd || !systemTypes.length) {
      throw new Error(`Date insuficiente pentru actualizarea mentenantei #${record?.number ?? '-'}.`);
    }

    const nextTechnicianId = updates.assignedTechnicianId ?? getAssignedTechnicianId(record) ?? null;
    const currentStatusId = getMaintenanceStatusId(record);
    let nextStatus = currentStatusId ?? 1;
    if (currentStatusId === 1 && nextTechnicianId) nextStatus = 2;
    if (currentStatusId === 5 && (updates.assignedTechnicianId !== undefined || updates.appointment !== undefined))
      nextStatus = 2;
    const selectedTechnician = technicians.find((technician: any) => technician?.id === nextTechnicianId);
    const historyEntry = `[Calendar dispatch update] - ${dayjs().format('DD-MM-YYYY HH:mm')}`;

    const payload = {
      id: record.id,
      number: record.number,
      contractId: record.contractId,
      statusId: nextStatus,
      status: record.status,
      companyId,
      company: record.company,
      workPlaceId,
      workPlace: record.workPlace,
      assignedTechnicianId: nextTechnicianId,
      assignedTechnician: selectedTechnician ?? record.assignedTechnician,
      registeredByUserId: record.registeredByUserId,
      interventionFileId: record.interventionFileId,
      finalDate: record.finalDate,
      periodicity,
      dateEnd,
      appointment: nextAppointment,
      intervalTimetable: record.intervalTimetable,
      history: [...(record.history ?? []), historyEntry],
      mentenancePlanningSystemTypes: systemTypes
        .map((entry: any) => ({
          id: entry?.id,
          mentenancePlanningId: entry?.mentenancePlanningId ?? record.id,
          systemType: entry?.systemType,
          systemTypeId: entry?.systemTypeId ?? entry?.systemType?.id,
        }))
        .filter((entry: any) => !!entry.systemTypeId),
    };

    const response = await MentenancePlanningService.upsert(record.id, payload);
    if (response?.errors?.length) {
      throw new Error(response.errors.join(', '));
    }

    return response?.response;
  };

  const handleViewDateChange = (value: Dayjs | null) => {
    if (!value) return;
    setFocusDate(value);
    calendarRef.current?.getApi()?.gotoDate(value.toDate());
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    const next = dayjs(arg.view.currentStart);
    const unit = viewMode === 'year' ? 'year' : viewMode === 'month' ? 'month' : 'day';
    // Defer calendar-driven state update to avoid lifecycle flush warnings.
    queueMicrotask(() => {
      setFocusDate((prev) => (prev.isSame(next, unit) ? prev : next));
    });
  };

  const handleEventClick = (eventClick: EventClickArg) => {
    const maintenanceId = eventClick.event.id;
    setSelectedMaintenanceIds((previous) => {
      if (previous.includes(maintenanceId)) {
        return previous.filter((value) => value !== maintenanceId);
      }

      return [...previous, maintenanceId];
    });
  };

  const handleStatusLegendToggle = (statusId: number) => {
    setSelectedStatusFilterIds((previous) => {
      if (previous.includes(statusId)) {
        return previous.filter((id) => id !== statusId);
      }

      return [...previous, statusId];
    });
  };

  const handleEventDrop = async (eventDrop: EventDropArg) => {
    const droppedRecord = eventDrop.event.extendedProps?.record as MaintenanceCalendarRow | undefined;
    if (!droppedRecord || !eventDrop.event.start || !eventDrop.oldEvent.start) {
      eventDrop.revert();
      return;
    }

    if (!canEditMaintenance(droppedRecord)) {
      message.warning(`Mentenanta #${droppedRecord.number ?? '-'} nu poate fi modificata din calendar.`);
      eventDrop.revert();
      return;
    }

    const daysDiff = dayjs(eventDrop.event.start)
      .startOf('day')
      .diff(dayjs(eventDrop.oldEvent.start).startOf('day'), 'day');

    const selectedGroup =
      selectedVisibleMaintenances.length > 1 && selectedMaintenanceIds.includes(droppedRecord.id)
        ? selectedVisibleMaintenances
        : [droppedRecord];

    const editableMaintenances = selectedGroup.filter(canEditMaintenance);
    const skippedLockedMaintenancesCount = selectedGroup.length - editableMaintenances.length;

    if (!editableMaintenances.length) {
      message.warning('Mentenantele selectate nu pot fi mutate (status blocat).');
      eventDrop.revert();
      return;
    }

    setIsUpdating(true);
    try {
      const calendarApi = calendarRef.current?.getApi();
      const items: { id: string; appointment: string }[] = [];
      for (const record of editableMaintenances) {
        const currentEvent = calendarApi?.getEventById(record.id);
        const baseDate = dayjs(currentEvent?.start ?? record.appointment ?? record.dateEnd);
        if (!baseDate.isValid()) {
          throw new Error(`Mentenanta #${record.number ?? '-'} nu are data valida pentru mutare.`);
        }
        const appointment =
          record.id === droppedRecord.id
            ? dayjs(eventDrop.event.start as Date).format('YYYY-MM-DD')
            : baseDate.add(daysDiff, 'day').format('YYYY-MM-DD');
        items.push({ id: record.id, appointment });
      }

      const response = await MentenancePlanningService.bulkUpdate(items);
      const hasErrors = (response?.errors?.length ?? 0) > 0;
      await refetch();

      if (!hasErrors) {
        const successMessage =
          editableMaintenances.length > 1
            ? `Au fost mutate ${editableMaintenances.length} mentenante.`
            : `Mentenanta #${droppedRecord.number ?? '-'} a fost reprogramata.`;
        message.success(successMessage);
      } else {
        eventDrop.revert();
        message.error(response?.errors?.join(', ') ?? 'Nu s-a putut reprograma nicio mentenanta selectata.');
      }

      if (skippedLockedMaintenancesCount > 0) {
        message.warning(
          `${skippedLockedMaintenancesCount} mentenante au fost ignorate (status Finalizata nu poate fi modificat).`,
        );
      }
    } catch (error: any) {
      eventDrop.revert();
      message.error(error?.message ?? 'Nu s-a putut reprograma mentenanta.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleApplyBulkUpdate = async () => {
    if (!selectedMaintenances.length) {
      message.warning('Selectati mentenantele din calendar pentru actualizare bulk.');
      return;
    }

    if (!selectedBulkEligibleMaintenances.length) {
      message.warning(
        'Actualizarea bulk este disponibila doar pentru mentenante cu status editabil (exclus Finalizata).',
      );
      return;
    }

    if (!bulkTechnicianId && !bulkAppointmentDate) {
      message.warning('Selectati un tehnician si/sau o data pentru actualizare bulk.');
      return;
    }

    setIsUpdating(true);
    try {
      const appointment = bulkAppointmentDate ? bulkAppointmentDate.format('YYYY-MM-DD') : undefined;
      const items = selectedBulkEligibleMaintenances.map((record) => ({
        id: record.id,
        ...(appointment && { appointment }),
        ...(bulkTechnicianId && { assignedTechnicianId: bulkTechnicianId }),
      }));

      const response = await MentenancePlanningService.bulkUpdate(items);
      const hasErrors = response?.errors?.length;

      await refetch();

      const skippedByStatusCount = selectedMaintenances.length - selectedBulkEligibleMaintenances.length;

      if (!hasErrors) {
        message.success(`Au fost actualizate ${selectedBulkEligibleMaintenances.length} mentenante.`);
      } else {
        message.error(response?.errors?.join(', ') ?? 'Actualizarea bulk a esuat.');
      }

      if (skippedByStatusCount > 0) {
        message.warning(
          `${skippedByStatusCount} mentenante nu au fost actualizate (status Finalizata nu poate fi modificat).`,
        );
      }
    } catch (error: any) {
      message.error(error?.message ?? 'A aparut o eroare la actualizarea bulk.');
    } finally {
      setIsUpdating(false);
    }
  };

  const renderEventContent = (eventContent: EventContentArg) => {
    const record = eventContent.event.extendedProps?.record as MaintenanceCalendarRow | undefined;
    if (!record) return null;
    const statusId = getMaintenanceStatusId(record);
    const statusDisplay = getStatusDisplay(statusId, record?.status?.name);
    const assignedTechnicianName = getAssignedTechnicianName(record);
    const finalizedTechnicianNames = getInterventionEngineerNames(record, technicians);
    return (
      <div className='maintenance-calendar-event-content'>
        <div className='maintenance-calendar-event-header'>
          <span className='maintenance-calendar-event-number'>#{record.number ?? '-'}</span>
          <Tag color={statusDisplay.color} className='maintenance-calendar-event-status'>
            {statusDisplay.label}
          </Tag>
        </div>
        <div className='maintenance-calendar-event-text maintenance-calendar-event-client'>{getClientName(record)}</div>
        <div className='maintenance-calendar-event-text'>{getWorkPlaceName(record)}</div>
        <div className='maintenance-calendar-event-periodicity'>{record.periodicity ?? '—'}</div>
        {statusId === 4 ? (
          <>
            <div className='maintenance-calendar-event-text maintenance-calendar-event-tech'>
              Asignat planificare: {assignedTechnicianName}
            </div>
            <div className='maintenance-calendar-event-text maintenance-calendar-event-tech'>
              Finalizat de: {finalizedTechnicianNames.length ? finalizedTechnicianNames.join(', ') : '-'}
            </div>
          </>
        ) : (
          <div className='maintenance-calendar-event-text maintenance-calendar-event-tech'>
            Tehnician: {assignedTechnicianName}
          </div>
        )}
      </div>
    );
  };

  const selectAllForDayRef = useRef<(dateStr: string) => void>(() => {});
  selectAllForDayRef.current = (dateStr: string) => {
    const idsOnDay = calendarEvents.filter((e) => e.start === dateStr).map((e) => e.id);
    if (idsOnDay.length === 0) return;
    setSelectedMaintenanceIds((prev) => [...new Set([...prev, ...idsOnDay])]);
    message.info(`${idsOnDay.length} mentenante selectate.`);
  };

  const handleMoreLinkClick = (info: { date: Date; jsEvent: { preventDefault: () => void } }) => {
    const dateStr = dayjs(info.date).format('YYYY-MM-DD');
    setTimeout(() => {
      const popover = document.querySelector('.fc-more-popover');
      const popoverBody = popover?.querySelector('.fc-popover-body');
      if (!popoverBody?.firstChild || popoverBody.querySelector('.maintenance-calendar-select-all-bar')) return;
      const bar = document.createElement('div');
      bar.className = 'maintenance-calendar-select-all-bar';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ant-btn ant-btn-primary ant-btn-sm';
      btn.textContent = 'Selecteaza toate';
      btn.onclick = () => selectAllForDayRef.current(dateStr);
      bar.appendChild(btn);
      popoverBody.insertBefore(bar, popoverBody.firstChild);
    }, 50);
  };

  const dayCellContent = (arg: { date: Date; dayNumberText: string }) => (
    <div className='maintenance-calendar-day-cell-content'>
      <span className='maintenance-calendar-day-number'>{arg.dayNumberText}</span>
    </div>
  );

  return (
    <div className='maintenance-calendar-wrapper'>
      <Card style={{ marginBottom: 16, boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Segmented
              options={[
                { label: 'Saptamana', value: 'week' },
                { label: 'Luna', value: 'month' },
                { label: 'An', value: 'year' },
              ]}
              value={viewMode}
              onChange={(v) => setViewMode((v as CalendarViewMode) ?? 'month')}
            />
          </Col>
          <Col xs={24} md={6}>
            <DatePicker
              picker={viewMode === 'year' ? 'year' : viewMode === 'month' ? 'month' : 'week'}
              value={viewMode === 'week' ? getWeekStart(focusDate) : focusDate}
              onChange={handleViewDateChange}
              style={{ width: '100%' }}
              format={viewMode === 'year' ? 'YYYY' : viewMode === 'month' ? 'MM.YYYY' : 'DD.MM.YYYY'}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder='Filtru periodicitate'
              value={selectedPeriodicityFilter}
              allowClear
              onChange={(value) => setSelectedPeriodicityFilter(value)}
              style={{ width: '100%' }}
              options={PERIODICITY_OPTIONS.map((periodicity) => ({
                label: periodicity,
                value: periodicity,
              }))}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder='Filtru tehnician asignat'
              value={selectedTechnicianFilterId}
              allowClear
              showSearch
              optionFilterProp='label'
              onChange={(value) => setSelectedTechnicianFilterId(value)}
              style={{ width: '100%' }}
              options={technicianFilterOptions.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
            />
          </Col>
          <Col xs={24}>
            <Space wrap>
              <Tag color='cyan'>Afisate: {filteredMaintenances.length}</Tag>
              <Tag color='purple'>Selectate: {selectedMaintenances.length}</Tag>
              <Tag color='gold'>Eligibile bulk: {selectedBulkEligibleMaintenances.length}</Tag>
            </Space>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          {legendItems.map((legendItem) => (
            <Col key={legendItem.statusId}>
              <Tag
                onClick={() => handleStatusLegendToggle(legendItem.statusId)}
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  borderColor: legendItem.color,
                  backgroundColor: selectedStatusFilterIds.includes(legendItem.statusId) ? legendItem.color : '#ffffff',
                  color: selectedStatusFilterIds.includes(legendItem.statusId) ? '#ffffff' : legendItem.color,
                }}
              >
                {legendItem.label} ({legendItem.count})
              </Tag>
            </Col>
          ))}
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={17}>
          <Card style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}>
            <div style={{ position: 'relative' }} className={isMonthView ? 'maintenance-calendar-grid-square' : ''}>
              {(isLoadingMaintenances || isUpdating) && (
                <div className='maintenance-calendar-loading'>
                  <Spin size='large' />
                </div>
              )}

              <FullCalendar
                key={viewMode}
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin]}
                initialView={VIEW_TO_FULLCALENDAR[viewMode]}
                initialDate={focusDate.format('YYYY-MM-DD')}
                views={{
                  dayGridMonth: {
                    showNonCurrentDates: false,
                    fixedWeekCount: false,
                  },
                  dayGridWeek: {},
                }}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: '',
                }}
                locale='ro'
                firstDay={1}
                selectable={false}
                editable
                eventStartEditable
                eventDurationEditable={false}
                droppable={false}
                eventAllow={(_dropInfo, draggedEvent) => {
                  const record = draggedEvent.extendedProps?.record as MaintenanceCalendarRow | undefined;
                  return record ? canEditMaintenance(record) : false;
                }}
                events={calendarEvents}
                eventDrop={handleEventDrop}
                eventClick={handleEventClick}
                eventContent={renderEventContent}
                eventClassNames={(arg) =>
                  selectedMaintenanceIds.includes(arg.event.id) ? ['maintenance-calendar-event-selected'] : []
                }
                datesSet={handleDatesSet}
                dayCellContent={dayCellContent}
                moreLinkClick={handleMoreLinkClick}
                nowIndicator
                dayMaxEventRows={2}
                height='auto'
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={7}>
          <Card style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }} title='Actualizare bulk (tehnician + data)'>
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              <Text type='secondary'>
                Click pe mentenante pentru selectie multipla. Trasati evenimentele pentru reprogramare. Selectati
                tehnician si/sau data si apasati Aplica bulk (doar mentenante cu status editabil).
              </Text>
              <Select
                placeholder='Selectati tehnician'
                value={bulkTechnicianId}
                allowClear
                showSearch
                optionFilterProp='label'
                onChange={(value) => setBulkTechnicianId(value)}
                options={technicians.map((technician: any) => ({
                  label: technician?.name,
                  value: technician?.id,
                }))}
              />

              <DatePicker
                value={bulkAppointmentDate}
                onChange={(value) => setBulkAppointmentDate(value)}
                placeholder='Selectati data'
                style={{ width: '100%' }}
                format='DD-MM-YYYY'
              />

              <Button
                type='primary'
                onClick={handleApplyBulkUpdate}
                loading={isUpdating}
                disabled={!selectedBulkEligibleMaintenances.length}
              >
                Aplica bulk
              </Button>
            </Space>
          </Card>

          <Card
            style={{ marginTop: 16, boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}
            title='Mentenante selectate'
            extra={
              <Button
                size='small'
                onClick={() => setSelectedMaintenanceIds([])}
                disabled={!selectedMaintenances.length}
              >
                Deselecteaza toate
              </Button>
            }
          >
            {selectedMaintenances.length ? (
              <Space direction='vertical' size={8} style={{ width: '100%' }}>
                {selectedMaintenances.map((record) => {
                  const statusId = getMaintenanceStatusId(record);
                  const statusDisplay = getStatusDisplay(statusId, record?.status?.name);
                  const displayDate = statusId === 4 ? record.interventionFile?.deliveryDate : record.appointment;
                  const assignedTechnicianName = getAssignedTechnicianName(record);
                  const finalizedTechnicianNames = getInterventionEngineerNames(record, technicians);
                  return (
                    <div key={record.id} className='maintenance-calendar-selected-item'>
                      <Text strong>#{record.number ?? '-'}</Text>
                      <Text>{getClientName(record)}</Text>
                      <Text>{getWorkPlaceName(record)}</Text>
                      <Tag color={statusDisplay.color}>{statusDisplay.label}</Tag>
                      <Text type='secondary'>Data: {displayDate ? dayjs(displayDate).format('DD-MM-YYYY') : '-'}</Text>
                      {statusId === 4 ? (
                        <>
                          <Text type='secondary'>
                            Finalizat de: {finalizedTechnicianNames.length ? finalizedTechnicianNames.join(', ') : '-'}
                          </Text>
                          <Text type='secondary'>Asignat planificare: {assignedTechnicianName}</Text>
                        </>
                      ) : (
                        <Text type='secondary'>Tehnician: {assignedTechnicianName}</Text>
                      )}
                      <Text type='secondary'>
                        Sisteme: {getSystemNames(record).length ? getSystemNames(record).join(', ') : '-'}
                      </Text>
                    </div>
                  );
                })}
              </Space>
            ) : (
              <Text type='secondary'>Click pe mentenante in calendar pentru selectie bulk.</Text>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MentenancePlanningCalendarTab;
